import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import RichTextRenderer from '../components/common/RichTextRenderer';
import Spinner from '../components/common/Spinner';
import { FiArrowLeft, FiCopy, FiCheck } from 'react-icons/fi';

const LessonPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const token = useSelector(state => state.auth.token);
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchLesson = async () => {
      try {
        const res = await axios.get(`/api/lessons/slug/${slug}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setLesson(res.data);
      } catch (err) {
        setError(err.response?.data?.msg || 'Failed to load lesson');
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchLesson();
  }, [slug, token]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <Spinner />;

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Lesson Not Found</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-[#1086a4] text-white rounded-lg hover:bg-[#0d6e87] transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <FiArrowLeft size={18} />
          <span className="text-sm">Back</span>
        </button>

        <button
          onClick={handleCopyLink}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
        >
          {copied ? <FiCheck size={14} className="text-green-600" /> : <FiCopy size={14} />}
          {copied ? 'Copied!' : 'Copy Link'}
        </button>
      </div>

      {/* Lesson Title */}
      <h1 className="text-3xl font-bold text-[#1e2d40] mb-8">{lesson.title}</h1>

      {/* Lesson Content */}
      <RichTextRenderer
        content={lesson.content}
        className="text-[#1e2d40] lesson-content"
        fontSize="16px"
        lineHeight="1.7"
        allowInlineStyles={true}
      />
    </div>
  );
};

export default LessonPage;
